import React, { useEffect, useRef, useState } from "react";
import ewasteBinImg from "../../assets/images/E-Waste-bin.png";
import "./EWasteParticipationModal.css";
import useBackdropClose from "./useBackdropClose";

/*
 * E-Waste Collection Drive — decision / thank-you card.
 *
 * mode "gate" (post-login, shown by OutletRouteSchool after the Swachhata Hi
 * Seva popup is resolved, only while the school has NOT reported):
 *   1. "Has your school participated?"      Yes → form      No → step 2
 *   2. "Would you like to participate?"     Yes → form      No → Dashboard
 * mode "done": shown after the report is saved.
 *
 * Split card (prefix ewg-): copy on the left, an animated "gadgets into the
 * collection bin" scene on the right that reacts to the Yes/No hover.
 *
 * No API calls here — "No" only dismisses the popup for this session.
 */

const BURST_COLOURS = ["#1f9d4d", "#7ed957", "#19b3a6", "#ffb020", "#ffffff", "#3fa7ff"];
const BURST = Array.from({ length: 40 }, (_, k) => {
  const angle = (k * 137.5 * Math.PI) / 180;
  const dist = 80 + ((k * 53) % 160);
  return {
    "--tx": `${Math.round(Math.cos(angle) * dist)}px`,
    "--ty": `${Math.round(Math.sin(angle) * dist - 50)}px`,
    "--r": `${(k * 97) % 720}deg`,
    "--cl": BURST_COLOURS[k % BURST_COLOURS.length],
    "--w": `${6 + (k % 3) * 3}px`,
  };
});

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Arrow = () => (
  <svg className="ewg-choice__arrow" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 10h12M11 5l5 5-5 5" />
  </svg>
);

const YesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" />
    <path d="m7.5 12.3 3 3 6-6.3" />
  </svg>
);

const NoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9.5" />
    <path d="M12 7v5l3 2" />
  </svg>
);

/* ── Scene: gadgets drop into the collection bin ─────────────────────────── */

// Each gadget is drawn centred on (0,0) and falls into the bin mouth at x.
const GADGETS = [
  {
    x: 190,
    from: -60,
    body: (
      <>
        <rect x="-11" y="-19" width="22" height="38" rx="4" fill="#263238" />
        <rect x="-8" y="-15" width="16" height="26" rx="1.5" fill="#6fd3ff" />
        <circle cx="0" cy="15" r="1.6" fill="#cfd8dc" />
      </>
    ),
  },
  {
    x: 232,
    from: 70,
    body: (
      <>
        <rect x="-24" y="-16" width="48" height="30" rx="3" fill="#37474f" />
        <rect x="-20" y="-12" width="40" height="22" rx="1.5" fill="#9be7a8" />
        <path d="M-30 14h60l-4 5h-52Z" fill="#90a4ae" />
      </>
    ),
  },
  {
    x: 208,
    from: -90,
    body: (
      <>
        <rect x="-4" y="-21" width="8" height="4" rx="1.5" fill="#90a4ae" />
        <rect x="-9" y="-17" width="18" height="36" rx="3" fill="#1b2a24" stroke="#7ed957" strokeWidth="2" />
        <path d="M1 -10-5 3h5l-2 9 7-13H1Z" fill="#ffd54f" />
      </>
    ),
  },
  {
    x: 222,
    from: 100,
    body: (
      <>
        <path d="M0-20a13 13 0 0 1 8 23v5h-16v-5A13 13 0 0 1 0-20Z" fill="#fff3c4" stroke="#ffb020" strokeWidth="2" />
        <rect x="-7" y="9" width="14" height="9" rx="2" fill="#90a4ae" />
        <path d="M-4 0q4-6 8 0" stroke="#ffb020" strokeWidth="2" fill="none" />
      </>
    ),
  },
  {
    x: 198,
    from: -40,
    body: (
      <>
        <path d="M-16 4v-4a16 16 0 0 1 32 0v4" stroke="#37474f" strokeWidth="4" fill="none" strokeLinecap="round" />
        <rect x="-21" y="0" width="10" height="16" rx="4" fill="#19b3a6" />
        <rect x="11" y="0" width="10" height="16" rx="4" fill="#19b3a6" />
      </>
    ),
  },
  {
    x: 214,
    from: 60,
    body: (
      <>
        <rect x="-16" y="-16" width="32" height="32" rx="3" fill="#146c43" stroke="#7ed957" strokeWidth="1.5" />
        <rect x="-7" y="-7" width="14" height="14" rx="1" fill="#ffd54f" />
        {[-10, 0, 10].map((p) => (
          <g key={p} stroke="#b8f5c8" strokeWidth="2" strokeLinecap="round">
            <path d={`M${p} -16v-6M${p} 16v6M-16 ${p}h-6M16 ${p}h6`} />
          </g>
        ))}
      </>
    ),
  },
];

const CHIPS = [
  { label: "IT & telecom", cls: "a" },
  { label: "Batteries & bulbs", cls: "b" },
  { label: "Lab instruments", cls: "c" },
  { label: "Toys & sports", cls: "d" },
];

const Scene = ({ mood }) => (
  <div className={`ewg-stage ewg-stage--${mood}`} aria-hidden="true">
    <svg className="ewg-circuit" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
      <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-10 70h90l30 30h80" />
        <path d="M410 120h-70l-26 26v70" />
        <path d="M-10 250h50l24-24h60" />
        <path d="M410 330h-60l-30 30h-40" />
        <path d="M60 610v-80l30-30v-50" />
        <path d="M340 610v-60l-24-24h-36" />
        <path d="M200 -10v40l-30 30" />
      </g>
      <g className="ewg-circuit__pulse" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-10 70h90l30 30h80" />
        <path d="M410 330h-60l-30 30h-40" style={{ animationDelay: "1.6s" }} />
        <path d="M60 610v-80l30-30v-50" style={{ animationDelay: "3.1s" }} />
        <path d="M410 120h-70l-26 26v70" style={{ animationDelay: "2.2s" }} />
      </g>
      <g fill="currentColor">
        {[[190, 100], [314, 216], [124, 226], [280, 360], [90, 450], [280, 526], [170, 60]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" />
        ))}
      </g>
    </svg>

    <div className="ewg-glow" />

    <svg className="ewg-scene" viewBox="0 0 420 460" preserveAspectRatio="xMidYMax meet">
      <g className="ewg-ring">
        <circle cx="210" cy="250" r="150" fill="none" stroke="rgba(126, 217, 87, 0.16)" strokeWidth="2" strokeDasharray="4 10" />
        {[0, 120, 240].map((a) => (
          <g key={a} transform={`rotate(${a} 210 250)`}>
            <path d="M198.5 118.5A132 132 0 0 1 337.5 215.8" fill="none" stroke="#7ed957" strokeWidth="10" strokeLinecap="round" opacity="0.55" />
            <path d="M343.6 238.8 353.3 211.6 321.7 219.9Z" fill="#7ed957" opacity="0.55" />
          </g>
        ))}
      </g>

      <ellipse className="ewg-floor" cx="210" cy="438" rx="150" ry="16" />

      {GADGETS.map((g, k) => (
        <g key={k} transform={`translate(${g.x} 300)`}>
          <g className="ewg-drop" style={{ "--from": `${g.from}px`, "--d": `${k * 1.2}s`, "--spin": `${k % 2 ? -300 : 320}deg` }}>
            {g.body}
          </g>
        </g>
      ))}

      <g className="ewg-bin">
        <image href={ewasteBinImg} x="70" y="236" width="280" height="193" preserveAspectRatio="xMidYMid meet" />
      </g>

      {[0, 1, 2, 3, 4, 5].map((k) => (
        <g key={k} transform={`translate(${GADGETS[k].x} 284)`}>
          <g className="ewg-spark" style={{ "--d": `${k * 1.2}s` }}>
            <path d="M0-14 3-3 14 0 3 3 0 14-3 3-14 0-3-3Z" fill="#ffe27a" />
          </g>
        </g>
      ))}
    </svg>

    <div className="ewg-chips">
      {CHIPS.map((c) => (
        <span key={c.label} className={`ewg-chip ewg-chip--${c.cls}`}>
          <i />
          {c.label}
        </span>
      ))}
    </div>
  </div>
);

const Brand = ({ kicker, small }) => (
  <>
    <div className="ewg-brand">
      <span className="ewg-logo">
        <img src={ewasteBinImg} alt="" />
      </span>
      <span className="ewg-kicker">
        <i aria-hidden="true" />
        {kicker}
      </span>
    </div>
    <h2 id="ewg-title" className={`ewg-title${small ? " ewg-title--sm" : ""}`}>
      <span className="ewg-accent">E-Waste</span> <span>Collection Drive</span>
    </h2>
  </>
);

/* ── Encourage steps ─────────────────────────────────────────────────────── */

const STEPS = [
  {
    title: "Set up a collection corner",
    text: "A safe, dry corner or bin for old computers, phones, cables, batteries and bulbs.",
    bg: "#e5f5e8",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true">
        <path d="M6 12h22l-2.4 18H8.4Z" fill="#22a447" />
        <rect x="4" y="8" width="26" height="5" rx="2" fill="#15803d" />
        <path d="m13.5 19.5 3.5-5 3.5 5M17 14.8v10" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    ),
  },
  {
    title: "Hold awareness activities",
    text: "Workshops, poster making, essays, quizzes or a rally with your students.",
    bg: "#fff1e0",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true" fill="none" stroke="#e07a12" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 14v6h5l11 6V8L10 14Z" fill="#ffd8a8" />
        <path d="M26 12q3 5 0 10M29 9q5 8 0 16" />
      </svg>
    ),
  },
  {
    title: "Report item-wise quantities",
    text: "Record items under IT & telecom, tools, toys & sports, lab instruments and others.",
    bg: "#e1f4f2",
    icon: (
      <svg viewBox="0 0 34 34" aria-hidden="true">
        <rect x="7" y="6" width="20" height="25" rx="3" fill="#19a99c" />
        <rect x="12" y="3.5" width="10" height="5" rx="2" fill="#0f766e" />
        <path d="M11.5 16h11M11.5 21h11M11.5 26h7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function EWasteParticipationModal({ isOpen, mode = "gate", created = false, onFill, onDismiss, onDone }) {
  const [step, setStep] = useState(1);
  const [mood, setMood] = useState("idle");
  const [burst, setBurst] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen, mode]);

  useEffect(() => {
    setMood("idle");
    setBurst(null);
    clearTimeout(timer.current);
  }, [step, mode, isOpen]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Clicking the dimmed area around the card: "Dashboard" on the thank-you
  // step, otherwise the same as "Not now".
  const backdrop = useBackdropClose(mode === "done" ? onDone : onDismiss);

  if (!isOpen) return null;

  const chooseYes = (e) => {
    if (burst) return;
    if (reducedMotion()) return onFill();
    const card = e.currentTarget.closest(".ewg-card").getBoundingClientRect();
    const btn = e.currentTarget.getBoundingClientRect();
    setMood("yes");
    setBurst({ x: btn.left + btn.width / 2 - card.left, y: btn.top + btn.height / 2 - card.top });
    timer.current = setTimeout(onFill, 650);
  };

  const hover = (m) => ({
    onMouseEnter: () => !burst && setMood(m),
    onFocus: () => !burst && setMood(m),
    onMouseLeave: () => !burst && setMood("idle"),
    onBlur: () => !burst && setMood("idle"),
  });

  let copy;
  if (mode === "done") {
    copy = (
      <>
        <div className="ewg-medal" aria-hidden="true">
          <svg viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="30" fill="#1f9d4d" />
            <circle cx="32" cy="32" r="24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
            <path className="ewg-tick" d="M19 33.5 28 42l17-19" fill="none" stroke="#fff" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span className="ewg-kicker">
          <i aria-hidden="true" />
          E-Waste Collection Drive
        </span>
        <h2 id="ewg-title" className="ewg-title">
          <span className="ewg-accent">Thank you!</span>
        </h2>
        <p className="ewg-lead">
          {created
            ? "Your school's E-Waste Collection Drive report has been submitted."
            : "Your school's E-Waste Collection Drive report has been updated."}{" "}
          You can update the details any time from the E-Waste Collection Drive card on your dashboard.
        </p>
        <div className="ewg-choices ewg-choices--done">
          <button type="button" className="ewg-choice ewg-choice--yes" onClick={onDone} {...hover("yes")}>
            <span className="ewg-choice__text">
              <b>Go to Dashboard</b>
            </span>
            <Arrow />
          </button>
          <button type="button" className="ewg-choice ewg-choice--no" onClick={onFill}>
            <span className="ewg-choice__text">
              <b>View details</b>
            </span>
          </button>
        </div>
      </>
    );
  } else if (step === 1) {
    copy = (
      <>
        <Brand kicker="Mission LiFE Initiative" />
        <p className="ewg-tagline">Responsible collection and recycling of old electronic and electrical items from schools.</p>
        <p className="ewg-q">Has your school participated in the E-Waste Collection Drive?</p>
        <div className="ewg-choices">
          <button type="button" className="ewg-choice ewg-choice--yes" onClick={chooseYes} {...hover("yes")}>
            <span className="ewg-choice__icon"><YesIcon /></span>
            <span className="ewg-choice__text">
              <b>Yes</b>
              <small>Fill in our report</small>
            </span>
            <Arrow />
          </button>
          <button type="button" className="ewg-choice ewg-choice--no" onClick={() => setStep(2)} {...hover("no")}>
            <span className="ewg-choice__icon"><NoIcon /></span>
            <span className="ewg-choice__text">
              <b>No</b>
              <small>Not yet</small>
            </span>
            <Arrow />
          </button>
        </div>
      </>
    );
  } else {
    copy = (
      <>
        <Brand kicker="Join Mission LiFE" small />
        <p className="ewg-lead">Collect old electronic items safely, raise awareness among students and report what your school collected.</p>
        <ol className="ewg-steps">
          {STEPS.map((s, k) => (
            <li key={s.title} className="ewg-step">
              <span className="ewg-step__icon" style={{ "--bg": s.bg }}>
                {s.icon}
                <em>{k + 1}</em>
              </span>
              <span>
                <b>{s.title}</b>
                <span>{s.text}</span>
              </span>
            </li>
          ))}
        </ol>
        <p className="ewg-q ewg-q--sm">Would you like to participate in the E-Waste Collection Drive?</p>
        <div className="ewg-choices">
          <button type="button" className="ewg-choice ewg-choice--yes" onClick={chooseYes} {...hover("yes")}>
            <span className="ewg-choice__icon"><YesIcon /></span>
            <span className="ewg-choice__text">
              <b>Yes</b>
              <small>Fill the report</small>
            </span>
            <Arrow />
          </button>
          <button type="button" className="ewg-choice ewg-choice--no" onClick={onDismiss} {...hover("no")}>
            <span className="ewg-choice__icon"><NoIcon /></span>
            <span className="ewg-choice__text">
              <b>No</b>
              <small>Go to Dashboard</small>
            </span>
            <Arrow />
          </button>
        </div>
        <button type="button" className="ewg-back" onClick={() => setStep(1)}>
          ← Back to previous question
        </button>
      </>
    );
  }

  return (
    <div className="ewaste-gate-backdrop" role="dialog" aria-modal="true" aria-labelledby="ewg-title" {...backdrop}>
      <div className="ewg-card" key={mode === "done" ? "done" : step}>
        <div className="ewg-copy">{copy}</div>
        <Scene mood={mode === "done" ? "yes" : mood} />
        {burst && (
          <div className="ewg-burst" style={{ left: burst.x, top: burst.y }} aria-hidden="true">
            {BURST.map((s, k) => (
              <span key={k} style={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
